import React, {useState} from "react";
import {twMerge} from "tailwind-merge";
import {Button} from "@vesta/aergia/components/button";
import StatusInformationIcon from "@vesta/aergia/svgs/core/status-information.svg?react";
import StatusSuccessIcon from "@vesta/aergia/svgs/core/status-success.svg?react";
import StatusWarningIcon from "@vesta/aergia/svgs/core/status-warning.svg?react";
import StatusErrorIcon from "@vesta/aergia/svgs/core/status-error.svg?react";
import StatusDiscoveryIcon from "@vesta/aergia/svgs/core/status-discovery.svg?react";
import CrossIcon from "@vesta/aergia/svgs/core/cross.svg?react";
import {tw} from "@vesta/aergia/utilities/utilities";
import {Link} from "@tanstack/react-router";

export function SectionMessage({
    appearance = "information",
    children,
    title,
    actions,
    isDismissible,
    isSurfaceless = false,
    onDismiss,
    icon,
    elemAfter: ElemAfterComponent,
    className,
    alignText = "left",
}: {
    appearance?: "information" | "warning" | "error" | "success" | "discovery";
    children: React.ReactNode;
    title?: string;
    actions?: React.ReactElement | React.ReactElement[];
    isDismissible?: boolean;
    onDismiss?: () => void;
    icon?: React.ReactNode;
    elemAfter?: React.ReactNode;
    className?: string;
    alignText?: "left" | "center";
    isSurfaceless?: boolean;
}) {
    const [isVisible, setIsVisible] = useState(true);

    const handleDismiss = () => {
        setIsVisible(false);
        onDismiss?.();
    };
    const backgroundClassName =
        isSurfaceless === true
            ? ""
            : appearance === "warning"
            ? tw`bg-ds-background-warning`
            : appearance === "success"
                ? tw`bg-ds-background-success`
                : appearance === "error"
                    ? tw`bg-ds-background-danger`
                    : appearance === "discovery"
                        ? tw`bg-ds-background-discovery`
                        : tw`bg-ds-background-information`;

    const IconComponent =
        appearance === "warning" ? (
            <StatusWarningIcon className="fill-ds-icon-warning" />
        ) : appearance === "success" ? (
            <StatusSuccessIcon className="fill-ds-icon-success" />
        ) : appearance === "error" ? (
            <StatusErrorIcon className="fill-ds-icon-danger" />
        ) : appearance === "discovery" ? (
            <StatusDiscoveryIcon className="fill-ds-icon-discovery" />
        ) : appearance === "information" ? (
            <StatusInformationIcon className="fill-ds-icon-information" />
        ) : (
            icon
        );
    const actionsArray = Array.isArray(actions) ? actions : actions ? [actions] : [];

    if (isVisible === false) {
        return null;
    }
    return (
        <div
            className={twMerge(
                backgroundClassName,
                isSurfaceless === true ? "p-0" : "p-ds-space-200 rounded-ds-border-radius-100",
            )}
        >
            <div className={twMerge("grid grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-ds-space-100 sm:gap-ds-space-200", className)}>
                {Boolean(IconComponent) && <div className="grid mt-ds-space-050">{IconComponent}</div>}
                <div className="grid gap-ds-space-100">
                    {Boolean(title) && (
                        <h2 className={twMerge("text-ds-text text-ds-font-heading-small", alignText === "center" ? "text-center" : "text-left")}>{title}</h2>
                    )}
                    {Boolean(children) && alignText === "left" && (
                        <div
                            className="text-ds-text text-ds-font-body"
                            style={{wordBreak: "break-word"}}
                        >
                            {children}
                        </div>
                    )}
                    <div className="sm:hidden grid">{Boolean(ElemAfterComponent) && <>{ElemAfterComponent}</>}</div>
                    {actionsArray.length > 0 && (
                        <div className="flex flex-wrap items-center gap-ds-space-100 text-ds-text text-ds-font-body">
                            {actionsArray.map((action, index) => (
                                <React.Fragment key={index}>
                                    {index > 0 && (
                                        <div className="flex items-center justify-center text-ds-text-subtle leading-none">
                                            <span className="inline-block align-middle">•</span>
                                        </div>
                                    )}
                                    {action}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>
                <div className="hidden sm:grid">{Boolean(ElemAfterComponent) && <>{ElemAfterComponent}</>}</div>
                {Boolean(isDismissible) && (
                    <Button
                        appearance="subtle"
                        onClick={handleDismiss}
                    >
                        <CrossIcon />
                    </Button>
                )}
            </div>
            {Boolean(children) && alignText === "center" && (
                <div
                    className="text-ds-text text-ds-font-body text-center"
                    style={{wordBreak: "break-word"}}
                >
                    {children}
                </div>
            )}
        </div>
    );
}

export function SectionMessageAction({href, children}: {href: string; children: React.ReactNode}) {
    return (
        <div className="text-ds-link underline">
            <Link to={href}>{children}</Link>
        </div>
    );
}
